// Download-format options for an approved download request.
//
// What's offered depends on what the approver granted on the request:
//   - "Original" when download_original is granted (images are always original-only).
//   - one option per MP4 quality variant when download_mp4 is granted.
//
// `type` is the value the download_zip API expects: "" means Original, otherwise
// the quality string (e.g. "360p"). `sizeMb` (video_pipeline.file_size for the
// original, video_pipeline_variants.video_size for a rendition — both stored in
// MB, read as MB everywhere in the app) and `ext` feed the download menu's
// second line; both are null when unknown.
const extOf = (name) => {
    const m = typeof name === 'string' ? name.match(/\.([a-z0-9]{2,5})(?:[?#].*)?$/i) : null;
    return m ? m[1].toUpperCase() : null;
};
const mbOf = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
};

// A request row can be downloaded only while it is APPROVED and its file is still live
// (approved, active). Since 2026-10-25 the server refuses a request whose file staff
// later rejected or deactivated, so the page must not offer it. Older response shapes
// without the two file fields are treated as live (the server still decides).
export function isDownloadableRequest(row) {
    return !!row && row.status === 'APPROVED'
        && row.isActive !== false
        && (row.approveStatus == null || row.approveStatus === 'Approved');
}

export function getDownloadFormatOptions(data) {
    const opts = [];
    if (!data) return opts;
    // eslint-disable-next-line eqeqeq
    const isImage = data.fileTypeId == 1;
    if (data.download_original || isImage) {
        opts.push({
            label: 'Original',
            type: '',
            sizeMb: mbOf(data.fileSize ?? data.file_size ?? data.size),
            // videoUrl is withheld for video originals (2026-10-25); fileName carries the extension.
            ext: extOf(data.videoName) || extOf(data.fileName) || extOf(data.videoUrl),
        });
    }
    if (data.download_mp4) {
        (data.qualityTypeArray || []).forEach((q) => {
            if (q && q.qualitytype) {
                opts.push({ label: q.qualitytype, type: q.qualitytype, sizeMb: mbOf(q.qualityvideosize), ext: 'MP4' });
            }
        });
    }
    return opts;
}

// One item for the prepare endpoint: the asset, the chosen version, and the
// request ROW it is downloaded from. Requests are per project (2026-09-23):
// the server authorises a regular user's download against that specific row
// (theirs, for this file, approved), never against "any approved row for the
// file" — so the row id must travel with every item. A legacy row without
// one sends null (the server reads null and undefined alike as "no row").
export function toDownloadItem(row, type) {
    return { fileId: row.videoId, type, requestId: row.requestVideoDownloadId ?? null };
}

// Versions offered for a whole request's ZIP (My Requests header button),
// named exactly like the per-asset menu: "Original" plus one entry per MP4
// quality found on ANY approved asset (best first). One choice applies to
// EVERY approved asset: under a quality, an asset that lacks it gets its
// closest available rendition (the lower one on a tie), and an asset with no
// renditions at all (images, un-transcoded videos) rides along as its
// Original. An asset with nothing downloadable (MP4-only grant, no renditions)
// is left out and the option's note says so. Qualities whose file list would
// be identical to an earlier option are not listed twice.
// Each option carries `items` = [{fileId, type, requestId}] for the prepare endpoint.
const qualityRank = (type) => parseInt(String(type), 10) || 0;

export function getGroupZipOptions(rows) {
    const approved = (rows || []).filter(isDownloadableRequest);
    const total = approved.length;
    if (!total) return [];
    const perAsset = approved.map((r) => ({ row: r, options: getDownloadFormatOptions(r) }));
    const original = (opts) => opts.find((o) => !o.type) || null;
    const closest = (opts, want) => {
        const target = qualityRank(want);
        const gap = (o) => Math.abs(qualityRank(o.type) - target);
        let pick = null;
        for (const o of opts) {
            if (!o.type) continue;
            if (!pick || gap(o) < gap(pick) || (gap(o) === gap(pick) && qualityRank(o.type) < qualityRank(pick.type))) pick = o;
        }
        return pick || original(opts);
    };

    const build = (label, type, pick) => {
        const items = [];
        let sizeMb = 0;
        let sizeKnown = true;
        for (const a of perAsset) {
            const o = pick(a.options);
            if (!o) continue;
            items.push(toDownloadItem(a.row, o.type));
            if (o.sizeMb) sizeMb += o.sizeMb; else sizeKnown = false;
        }
        if (!items.length) return null;
        return {
            label, type, items,
            sizeMb: sizeMb || null, sizeApprox: !sizeKnown, ext: null,
            note: items.length < total ? `${items.length} of ${total} assets` : `${total} asset${total === 1 ? '' : 's'}`,
            skipped: total - items.length,
        };
    };
    const same = (a, b) => JSON.stringify(a.items) === JSON.stringify(b.items);

    const qualities = [...new Set(perAsset.flatMap((a) => a.options.filter((o) => o.type).map((o) => o.type)))]
        .sort((a, b) => qualityRank(b) - qualityRank(a));
    const candidates = [build('Original', '', original), ...qualities.map((q) => build(q, q, (opts) => closest(opts, q)))];
    return candidates.filter(Boolean).filter((o, i, arr) => arr.slice(0, i).every((p) => !same(p, o)));
}
